import React from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  ChipProps,
  Box,
  Typography,
  Checkbox,
  ListItem,
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox, SelectAll } from '@mui/icons-material';

export interface SelectOption {
  value: string;
  label: string;
}

export interface MultiSelectFieldProps {
  options: SelectOption[];
  value: SelectOption[];
  onChange: (value: SelectOption[]) => void;
  label: string;
  placeholder?: string;
  loading?: boolean;
  loadingText?: string;
  noOptionsText?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  chipColor?: ChipProps['color'];
  chipVariant?: ChipProps['variant'];
  maxChips?: number;
  renderChip?: (option: SelectOption, index: number, getTagProps: any) => React.ReactNode;
  enableSelectAll?: boolean;
  selectAllText?: string;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Selecione opções...',
  loading = false,
  loadingText = 'Carregando...',
  noOptionsText = 'Nenhuma opção encontrada',
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  size = 'medium',
  chipColor = 'primary',
  chipVariant = 'outlined',
  maxChips,
  renderChip,
  enableSelectAll = true,
  selectAllText = 'Selecionar todos',
}) => {
  const defaultRenderChip = (option: SelectOption, index: number, getTagProps: any) => (
    <Chip
      {...getTagProps({ index })}
      key={option.value}
      label={option.label}
      size="small"
      variant={chipVariant}
      color={chipColor}
      sx={{
        '&:hover': {
          backgroundColor: `${chipColor}.light`,
          color: `${chipColor}.contrastText`,
        }
      }}
    />
  );

  const allSelected = value.length === options.length;
  const someSelected = value.length > 0 && value.length < options.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options);
    }
  };

  const handleOptionChange = (event: any, newValue: SelectOption[]) => {
    const filteredValue = newValue.filter(item => item.value !== '__select_all__');
    onChange(filteredValue);
  };

  const selectAllOption: SelectOption = {
    value: '__select_all__',
    label: selectAllText
  };

  const enhancedOptions = enableSelectAll ? [selectAllOption, ...options] : options;

  return (
    <Autocomplete
      multiple
      options={enhancedOptions}
      getOptionLabel={(option: SelectOption) => option.label}
      value={value}
      onChange={handleOptionChange}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      disabled={disabled}
      disableCloseOnSelect
      renderOption={(props, option, { selected }) => {
        if (option.value === '__select_all__') {
          return (
            <ListItem
              {...props}
              key={option.value}
              onClick={(e) => {
                e.preventDefault();
                handleSelectAll();
              }}
              sx={{
                backgroundColor: 'action.hover',
                borderBottom: '2px solid',
                borderBottomColor: 'divider',
                marginBottom: 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                },
                '&.Mui-focused': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                  style={{ marginRight: 0 }}
                  checked={allSelected}
                  indeterminate={someSelected}
                  color="primary"
                />
                <Typography variant="body2" fontWeight="medium" color="primary">
                  {option.label}
                  {someSelected && ` (${value.length}/${options.length})`}
                </Typography>
              </Box>
            </ListItem>
          );
        }

        return (
          <ListItem {...props} key={option.value}>
            <Checkbox
              icon={<CheckBoxOutlineBlank fontSize="small" />}
              checkedIcon={<CheckBox fontSize="small" />}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          size={size}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => 
          renderChip 
            ? renderChip(option, index, getTagProps)
            : defaultRenderChip(option, index, getTagProps)
        )
      }
      isOptionEqualToValue={(option: SelectOption, value: SelectOption) => 
        option.value === value?.value
      }
      fullWidth={fullWidth}
      limitTags={maxChips}
    />
  );
};

export default MultiSelectField;
